import axios from "../axios";
import toast from "react-hot-toast";

/**
 * Credential Vault API Endpoints
 *
 * These endpoints communicate with the backend vault API.
 * All credential data is encrypted client-side before being sent.
 *
 * Vaults are company-level containers that hold multiple credential entries.
 * Vaults can be linked to multiple programs.
 */

export type CredentialAccessAction =
  | "view"
  | "decrypt"
  | "create"
  | "update"
  | "delete"
  | "export"
  | "failed_decrypt"
  | "failed_unlock";

// Constants for action types
export const CredentialAccessActions = {
  VIEW: "view" as const,
  DECRYPT: "decrypt" as const,
  CREATE: "create" as const,
  UPDATE: "update" as const,
  DELETE: "delete" as const,
  EXPORT: "export" as const,
  FAILED_DECRYPT: "failed_decrypt" as const,
  FAILED_UNLOCK: "failed_unlock" as const
};

export type CredentialType =
  | "password"
  | "api_key"
  | "ssh_key"
  | "certificate"
  | "database"
  | "server"
  | "secure_note"
  | "wifi"
  | "credit_card"
  | "custom";

// Individual credential entry within a vault
export type CredentialEntry = {
  entry_id: number;
  vault_id: number;
  label: string;
  credential_type?: CredentialType;
  url?: string;
  username?: string;
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  salt: string | null;  // Null for public key encryption (sub-users)
  key_iterations: number | null;  // Null for public key encryption
  encrypted_credential_key?: string | null;  // Present for public key encryption
  encrypted_notes?: string;
  notes_iv?: string;
  notes_auth_tag?: string;
  custom_fields?: string;
  expires_at?: string;
  last_rotated_at?: string;
  rotation_reminder_days?: number;
  strength_score?: number;
  is_favorite?: boolean;
  created_by: number;
  createdAt: string;
  updatedAt: string;
};

// Vault container with its entries
export type Vault = {
  vault_id: number;
  company_id: number;
  name: string;
  description: string | null;
  created_by: number;
  modified_by: number | null;
  createdAt: string;
  updatedAt: string;
  entries?: CredentialEntry[];
  creator?: {
    user_id: number;
    username: string;
    email: string;
  };
  modifier?: {
    user_id: number;
    username: string;
    email: string;
  };
  // RSA keys for asymmetric encryption (sub-user write-only access)
  public_key?: string;
  has_asymmetric_keys?: boolean;
  // Encrypted private key (for business owner to decrypt sub-user credentials)
  encrypted_private_key?: string;
  private_key_iv?: string;
  private_key_salt?: string;
  private_key_auth_tag?: string;
  // Soft delete fields
  deleted_at?: string | null;
  deleted_by?: number | null;
};

// Summary for dropdown selection
export type VaultSummary = {
  vault_id: number;
  name: string;
  description: string | null;
  createdAt: string;
};

// Access types for vault-program links
export type CredentialAccessType = "unlimited" | "view_once" | "time_limited";
export type AccessStatus = "available" | "expired" | "consumed";

// Re-encrypted entry for access password system
export type ReEncryptedEntry = {
  entry_id: number;
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  encrypted_notes?: string;
  notes_iv?: string;
  notes_auth_tag?: string;
  encrypted_custom_fields?: string;
  custom_fields_iv?: string;
  custom_fields_auth_tag?: string;
};

// Link vault options with access password
export type LinkVaultOptions = {
  accessType?: CredentialAccessType;
  expiresAt?: string;
  accessSalt: string;
  reEncryptedEntries: ReEncryptedEntry[];
  automatedAccessKey?: string;
};

// Program credential entry (re-encrypted with access password)
export type ProgramCredentialEntry = {
  id: number;
  entryId: number;
  label: string;
  credentialType: CredentialType;
  url?: string;
  username?: string;
  encryptedData: string;
  iv: string;
  authTag: string;
  encryptedNotes?: string;
  notesIv?: string;
  notesAuthTag?: string;
  encryptedCustomFields?: string;
  customFieldsIv?: string;
  customFieldsAuthTag?: string;
};

// Program vault link with credentials
export type ProgramVaultLink = {
  linkId: number;
  vault: {
    vault_id: number;
    name: string;
    description?: string;
  };
  vaultDeleted?: boolean;
  accessType: CredentialAccessType;
  expiresAt?: string;
  accessStatus: AccessStatus | "deleted";
  accessSalt?: string;
  automated_access_key?: string;
  entries: ProgramCredentialEntry[];
};

export type CredentialAccessLog = {
  log_id: number;
  vault_id: number;
  entry_id?: number;
  company_id: number;
  user_id: number;
  action: CredentialAccessAction;
  success: boolean;
  ip_address: string | null;
  user_agent: string | null;
  metadata: string | null;
  created_at: string;
  user?: {
    user_id: number;
    username: string;
    email: string;
    pfp?: string;
  };
  vault?: {
    vault_id: number;
    name: string;
  };
  entry?: {
    entry_id: number;
    name: string;
    credential_type: string;
  };
};

export type AuditTrailFilters = {
  page?: number;
  pageSize?: number;
  action?: CredentialAccessAction;
  entryId?: number;
  vaultId?: number;
  userId?: number;
  startDate?: string;
  endDate?: string;
};

export type AuditTrailResponse = {
  logs: CredentialAccessLog[];
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  summary: {
    actionCounts: Array<{ action: string; count: number }>;
  };
};

export type CreateVaultData = {
  name: string;
  description?: string;
  // RSA key pair for asymmetric encryption (sub-user write-only access)
  public_key?: string;
  encrypted_private_key?: string;
  private_key_iv?: string;
  private_key_salt?: string;
  private_key_auth_tag?: string;
};

export type UpdateVaultData = {
  name?: string;
  description?: string;
};

export type CreateCredentialEntryData = {
  label: string;
  credential_type?: CredentialType;
  url?: string;
  username?: string;
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  // salt and key_iterations are only required for symmetric (master password) encryption
  // For asymmetric (public key) encryption, these are not needed
  salt?: string;
  key_iterations?: number;
  encrypted_notes?: string;
  notes_iv?: string;
  notes_auth_tag?: string;
  custom_fields?: string;
  expires_at?: string;
  rotation_reminder_days?: number;
  strength_score?: number;
  is_favorite?: boolean;
  notes?: string;
  // For asymmetric encryption (sub-user mode) - RSA-encrypted AES key
  encrypted_credential_key?: string;
};

export type UpdateCredentialEntryData = {
  label?: string;
  credential_type?: CredentialType;
  url?: string;
  username?: string;
  encrypted_data?: string;
  iv?: string;
  auth_tag?: string;
  salt?: string;
  key_iterations?: number;
  encrypted_notes?: string;
  notes_iv?: string;
  notes_auth_tag?: string;
  custom_fields?: string;
  expires_at?: string;
  rotation_reminder_days?: number;
  strength_score?: number;
  is_favorite?: boolean;
  notes?: string;
};

/**
 * Sub-user permissions for a vault
 */
export interface VaultUserPermissions {
  can_add: boolean;
  can_link: boolean;
  can_view_audit: boolean;
}

export interface VerificationKeyData {
  encrypted_private_key: string;
  private_key_iv: string;
  private_key_salt: string;
  private_key_auth_tag: string;
}

/**
 * Response from getVaultsByCompany
 */
export interface GetVaultsResponse {
  vaults: Vault[];
  permissions?: Record<number, VaultUserPermissions>; // Only present for sub-users
  hasMasterPasswordConfigured?: boolean;
  verificationKey?: VerificationKeyData;
}

/**
 * Get all vaults for the user's company (with entries)
 * For sub-users, also returns their permissions per vault
 */
export const getVaultsByCompany = async (companyId?: number): Promise<GetVaultsResponse> => {
  try {
    const response = await axios.get("/v2/vault/company", {
      params: companyId ? { company_id: companyId } : {}
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching vaults:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Get all vaults linked to a program (with entries)
 */
export const getVaultsByProgram = async (
  programId: number
): Promise<{ vaults?: Vault[]; credentials?: Vault[] }> => {
  try {
    const response = await axios.get(`/v2/vault/program/${programId}/credentials`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching vaults:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Get available vaults for linking (dropdown data)
 */
export const getAvailableVaults = async (): Promise<{ vaults: VaultSummary[] }> => {
  try {
    const response = await axios.get("/v2/vault/available");
    return response.data;
  } catch (error: any) {
    console.error("Error fetching available vaults:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Get linked vault IDs for a program
 */
export const getLinkedVaultIds = async (
  programId: number
): Promise<{ vaultIds: number[] }> => {
  try {
    const response = await axios.get(`/v2/vault/program/${programId}/linked`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching linked vault IDs:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Create a new vault (container)
 */
export const createVault = async (
  data: CreateVaultData
): Promise<{ vault: Vault; message: string }> => {
  try {
    const response = await axios.post("/v2/vault", data);
    toast.success("Vault created successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error creating vault:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Update a vault
 */
export const updateVault = async (
  vaultId: number,
  data: UpdateVaultData
): Promise<{ vault: Vault; message: string }> => {
  try {
    const response = await axios.put(`/v2/vault/${vaultId}`, data);
    toast.success("Vault updated successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error updating vault:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Delete a vault (soft delete)
 */
export const deleteVault = async (
  vaultId: number
): Promise<{ message: string }> => {
  try {
    const response = await axios.delete(`/v2/vault/${vaultId}`);
    toast.success("Vault deleted");
    return response.data;
  } catch (error: any) {
    console.error("Error deleting vault:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Create a credential entry within a vault
 */
export const createCredentialEntry = async (
  vaultId: number,
  data: CreateCredentialEntryData
): Promise<{ entry: CredentialEntry; message: string }> => {
  try {
    const response = await axios.post(`/v2/vault/${vaultId}/entry`, data);
    toast.success("Credential saved securely");
    return response.data;
  } catch (error: any) {
    console.error("Error creating credential entry:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Update a credential entry
 */
export const updateCredentialEntry = async (
  vaultId: number,
  entryId: number,
  data: UpdateCredentialEntryData
): Promise<{ entry: CredentialEntry; message: string }> => {
  try {
    const response = await axios.put(`/v2/vault/${vaultId}/entry/${entryId}`, data);
    toast.success("Credential updated successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error updating credential entry:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Delete a credential entry (soft delete)
 */
export const deleteCredentialEntry = async (
  vaultId: number,
  entryId: number
): Promise<{ message: string }> => {
  try {
    const response = await axios.delete(`/v2/vault/${vaultId}/entry/${entryId}`);
    toast.success("Credential deleted");
    return response.data;
  } catch (error: any) {
    console.error("Error deleting credential entry:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Bulk import credential entries data type
 */
export type BulkImportEntryData = {
  label: string;
  credential_type?: CredentialType;
  url?: string;
  username?: string;
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  salt?: string; // Optional for asymmetric encryption
  key_iterations?: number;
  encrypted_notes?: string;
  notes_iv?: string;
  notes_auth_tag?: string;
  strength_score?: number;
  is_favorite?: boolean;
  expires_at?: string;
  rotation_reminder_days?: number;
  encrypted_credential_key?: string; // Added for asymmetric encryption
};

/**
 * Bulk import credentials into a vault
 * Only Business Owner can use this (requires master password)
 */
export const bulkImportCredentials = async (
  vaultId: number,
  entries: BulkImportEntryData[]
): Promise<{
  message: string;
  imported: number;
  failed: number;
  errors?: string[];
  entries: Array<{ entry_id: number; label: string; credential_type: string }>;
}> => {
  try {
    const response = await axios.post(`/v2/vault/${vaultId}/bulk-import`, { entries });
    toast.success(response.data.message);
    return response.data;
  } catch (error: any) {
    console.error("Error bulk importing credentials:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Link a vault to a program with access password
 *
 * The business owner must:
 * 1. Decrypt credentials with master password (client-side)
 * 2. Re-encrypt with access password (client-side)
 * 3. Send re-encrypted data to server
 */
export const linkVaultToProgram = async (
  vaultId: number,
  programId: number,
  options: LinkVaultOptions
): Promise<{ message: string; linkId: number; entriesCount: number }> => {
  try {
    const response = await axios.post(`/v2/vault/${vaultId}/link/${programId}`, options);
    toast.success("Credential linked to program with access password");
    return response.data;
  } catch (error: any) {
    console.error("Error linking vault:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Get credentials for a program (for researchers/pentesters)
 * Returns re-encrypted credentials that can be decrypted with access password
 */
export const getProgramCredentials = async (
  programId: number
): Promise<{ credentials: ProgramVaultLink[] }> => {
  try {
    const response = await axios.get(`/v2/vault/program/${programId}/credentials`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching program credentials:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Unlink a vault from a program
 */
export const unlinkVaultFromProgram = async (
  vaultId: number,
  programId: number
): Promise<{ message: string }> => {
  try {
    const response = await axios.delete(`/v2/vault/${vaultId}/link/${programId}`);
    toast.success("Credential unlinked from program");
    return response.data;
  } catch (error: any) {
    console.error("Error unlinking vault:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

// Linked program info type
export type LinkedProgramInfo = {
  programId: number;
  programTitle: string;
  profilePicture?: string;
  isActivated: boolean;
  linkedAt: string;
  accessType: CredentialAccessType;
};

/**
 * Get linked programs for a vault
 */
export const getLinkedProgramsForVault = async (
  vaultId: number
): Promise<{ linkedPrograms: LinkedProgramInfo[] }> => {
  try {
    const response = await axios.get(`/v2/vault/${vaultId}/linked-programs`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching linked programs:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

// Rate limit response type
export type RateLimitInfo = {
  failureCount: number;
  lockedOut: boolean;
  lockoutDurationMs?: number;
};

export type LogAccessResponse = {
  message: string;
  rateLimitInfo?: RateLimitInfo;
};

// Rate limit status response
export type RateLimitStatus = {
  isLocked: boolean;
  failuresInWindow: number;
  consecutiveFailures: number;
  nextAttemptAllowedAt?: string;
  lockoutEndsAt?: string;
};

/**
 * Check rate limit status for a vault before attempting decrypt
 */
export const checkRateLimitStatus = async (
  vaultId: number
): Promise<RateLimitStatus> => {
  try {
    const response = await axios.get(`/v2/vault/${vaultId}/rate-limit-status`);
    return response.data;
  } catch (error: any) {
    console.error("Error checking rate limit status:", error);
    // Return safe default - assume not limited
    return {
      isLocked: false,
      failuresInWindow: 0,
      consecutiveFailures: 0
    };
  }
};

/**
 * Log credential access (called when user views or decrypts)
 * Supports logging both successful and failed attempts for security audit
 *
 * IMPORTANT: This now returns rate limit information for failed attempts.
 * Throws an error with rate limit details if rate limited.
 */
export const logCredentialAccess = async (
  vaultId: number,
  action: CredentialAccessAction,
  options?: {
    programId?: number;
    success?: boolean;
    reason?: string;
    automatedAccessKey?: string;
  }
): Promise<LogAccessResponse> => {
  try {
    const response = await axios.post(`/v2/vault/${vaultId}/log-access`, {
      action,
      programId: options?.programId,
      success: options?.success ?? true,
      reason: options?.reason,
      automatedAccessKey: options?.automatedAccessKey
    });
    return response.data;
  } catch (error: any) {
    // Check if rate limited (429 status)
    if (error?.response?.status === 429) {
      const rateLimitError = new Error(error.response.data.message || "Too many failed attempts");
      (rateLimitError as any).rateLimited = true;
      (rateLimitError as any).lockedOut = error.response.data.lockedOut;
      (rateLimitError as any).retryAfterMs = error.response.data.retryAfterMs;
      (rateLimitError as any).failureCount = error.response.data.failureCount;
      throw rateLimitError;
    }
    // Silent fail for other errors - logging should not disrupt user experience
    console.error("Error logging credential access:", error);
    return { message: "Error logging access" };
  }
};

/**
 * Update automated access key on an existing program vault link
 */
export const updateVaultLinkAccessKey = async (
  programId: number,
  vaultId: number,
  accessKey: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.put(
      `/v2/vault/program/${programId}/vault/${vaultId}/automated-key`,
      { accessKey }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error updating vault access key:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Log failed decrypt attempt for security audit
 * Returns rate limit info if available
 */
export const logFailedDecrypt = async (
  vaultId: number,
  programId?: number,
  reason: string = "Invalid password"
): Promise<LogAccessResponse> => {
  return logCredentialAccess(vaultId, "failed_decrypt", {
    programId,
    success: false,
    reason
  });
};

/**
 * Consume access for view_once credentials
 * Called after a researcher views credentials with view_once access type
 */
export const consumeAccess = async (
  vaultId: number,
  programId: number
): Promise<{ message: string }> => {
  try {
    const response = await axios.post(`/v2/vault/${vaultId}/consume-access/${programId}`);
    return response.data;
  } catch (error: any) {
    console.error("Error consuming access:", error);
    throw error;
  }
};

/**
 * Get access logs for a credential
 */
export const getAccessLogs = async (
  vaultId: number,
  page: number = 1,
  pageSize: number = 20
): Promise<{
  logs: CredentialAccessLog[];
  pagination: { total: number; page: number; pageSize: number; totalPages: number };
}> => {
  try {
    const response = await axios.get(`/v2/vault/${vaultId}/logs`, {
      params: { page, pageSize }
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching access logs:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Get comprehensive audit trail for company's vault activity
 */
export const getAuditTrail = async (
  filters: AuditTrailFilters = {}
): Promise<AuditTrailResponse> => {
  try {
    const params: Record<string, string | number> = {};
    if (filters.page) params.page = filters.page;
    if (filters.pageSize) params.pageSize = filters.pageSize;
    if (filters.action) params.action = filters.action;
    if (filters.entryId) params.entryId = filters.entryId;
    if (filters.vaultId) params.vaultId = filters.vaultId;
    if (filters.userId) params.userId = filters.userId;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;

    const response = await axios.get("/v2/vault/audit-trail", { params });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching audit trail:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

// ============================================================================
// VAULT PERMISSION MANAGEMENT
// ============================================================================

export type VaultPermission = {
  permission_id: string;
  vault_id: number;
  user_id: number;
  can_add: boolean;
  can_link: boolean;
  can_view_audit: boolean;
  granted_by: number;
  revoked_at: string | null;
  revoked_by: number | null;
  createdAt: string;
  user?: {
    user_id: number;
    username: string;
    email: string;
    pfp: string | null;
    role: number;
  };
  grantor?: {
    user_id: number;
    username: string;
    email: string;
  };
};

export type SubUser = {
  user_id: number;
  username: string;
  email: string;
  pfp: string | null;
  role: number;
};

/**
 * Get all permissions for a vault
 */
export const getVaultPermissions = async (
  vaultId: number
): Promise<{ permissions: VaultPermission[] }> => {
  try {
    const response = await axios.get(`/v2/vault/${vaultId}/permissions`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching vault permissions:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Get available sub-users for permission assignment
 */
export const getAvailableSubUsers = async (
  vaultId: number
): Promise<{ subUsers: SubUser[] }> => {
  try {
    const response = await axios.get(`/v2/vault/${vaultId}/permissions/available-users`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching available sub-users:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Grant permission to a sub-user
 * Note: can_link is not supported - sub-users cannot link vaults (requires master password)
 */
export const grantVaultPermission = async (
  vaultId: number,
  userId: number,
  permissions: {
    can_add?: boolean;
    can_view_audit?: boolean;
  }
): Promise<{ message: string; permission: VaultPermission }> => {
  try {
    const response = await axios.post(`/v2/vault/${vaultId}/permissions`, {
      user_id: userId,
      can_add: permissions.can_add ?? false,
      can_view_audit: permissions.can_view_audit ?? false
    });
    toast.success("Permission granted successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error granting vault permission:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Revoke permission from a sub-user
 */
export const revokeVaultPermission = async (
  vaultId: number,
  permissionId: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.delete(`/v2/vault/${vaultId}/permissions/${permissionId}`);
    toast.success("Permission revoked successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error revoking vault permission:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

// ============================================================================
// SYNC STATUS MANAGEMENT
// ============================================================================

export type StaleLink = {
  id: string;
  vault_id: number;
  program_id: number;
  is_stale: boolean;
  stale_reason: string | null;
  last_synced_at: string | null;
  program?: {
    program_id: number;
    program_title: string;
  };
};

/**
 * Get stale program links for a vault
 */
export const getStaleLinks = async (
  vaultId: number
): Promise<{ staleLinks: StaleLink[] }> => {
  try {
    const response = await axios.get(`/v2/vault/${vaultId}/stale-links`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching stale links:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Re-sync a stale program link
 */
export const resyncProgramLink = async (
  vaultId: number,
  programId: number,
  entries: Array<{
    entry_id: number;
    encrypted_data: string;
    iv: string;
    auth_tag: string;
    encrypted_notes?: string;
    notes_iv?: string;
    notes_auth_tag?: string;
    encrypted_custom_fields?: string;
    custom_fields_iv?: string;
    custom_fields_auth_tag?: string;
  }>
): Promise<{ message: string }> => {
  try {
    const response = await axios.post(`/v2/vault/${vaultId}/resync/${programId}`, {
      entries
    });
    toast.success("Program link re-synced successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error re-syncing program link:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

// ============================================================================
// SUB-USER CREDENTIAL ENCRYPTION
// ============================================================================

/**
 * Get vault's public key for sub-user encryption
 * Sub-users with can_add permission use this to encrypt credentials
 */
export const getVaultPublicKey = async (
  vaultId: number
): Promise<{ vault_id: number; name: string; public_key: string }> => {
  try {
    const response = await axios.get(`/v2/vault/${vaultId}/public-key`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching vault public key:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

// ============================================================================
// MASTER PASSWORD SECURITY MANAGEMENT
// ============================================================================

export interface ChangeMasterPasswordPayload {
  new_encrypted_private_key: string;
  private_key_iv: string;
  private_key_salt: string;
  private_key_auth_tag?: string;
  public_key?: string;
  reencrypted_entries?: Array<{
    entry_id: number;
    encrypted_data: string;
    iv: string;
    auth_tag: string;
    salt?: string | null;
    key_iterations?: number | null;
    encrypted_notes?: string;
    notes_iv?: string;
    notes_auth_tag?: string;
  }>;
}

export interface ResetMasterPasswordPayload {
  new_encrypted_private_key: string;
  private_key_iv: string;
  private_key_salt: string;
  private_key_auth_tag?: string;
  public_key?: string;
  confirmation_text: string;
  accepted_terms: boolean;
}

// Backend returns updated key fields so the frontend can patch local vault state without a refetch
export interface UpdatedVaultKeys {
  vault_id: number;
  encrypted_private_key: string;
  private_key_iv: string;
  private_key_salt: string;
  private_key_auth_tag: string | null;
  public_key?: string;
}

/**
 * Change master password (re-encrypt private key and active entries).
 * The response includes `updated_vault` so callers can patch state without a refetch round-trip.
 */
export const changeMasterPassword = async (
  payload: ChangeMasterPasswordPayload
): Promise<{ message: string; reencrypted_count: number; updated_vault: UpdatedVaultKeys }> => {
  try {
    const response = await axios.post("/v2/vault/change-master-password", payload);
    toast.success("Master password changed successfully");
    return response.data;
  } catch (error: any) {
    console.error("Error changing master password:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Reset master password (emergency hard reset & keypair regeneration).
 * The response includes `updated_vault` so callers can patch state without a refetch round-trip.
 */
export const resetMasterPassword = async (
  payload: ResetMasterPasswordPayload
): Promise<{ message: string; timestamp: string; updated_vault: UpdatedVaultKeys }> => {
  try {
    const response = await axios.post("/v2/vault/reset-master-password", payload);
    toast.success("Master password reset successfully! Security alerts sent.");
    return response.data;
  } catch (error: any) {
    console.error("Error resetting master password:", error);
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    }
    throw error;
  }
};
